import { Injectable } from '@angular/core';
import { ModalService } from 'mnm-webapp';
import { ConfirmDialogComponent } from './confirm-dialog.component';
import {
    AlertDialogOptions,
    ConfirmDialogOptions,
    InputDialogOptions,
    MultiButtonDialogOptions,
} from './models';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Injectable()
export class ConfirmService {
    public constructor(private readonly modalService: ModalService) {}

    public confirm(options: ConfirmDialogOptions): Promise<boolean> {
        return new Promise<boolean>(resolve => {
            const destroy$ = new Subject<void>();

            this.modalService
                .show(ConfirmDialogComponent, {
                    title: options.title,
                    beforeInit: c => {
                        c.mode = 'confirm';
                        c.text = options.text;
                        c.icon = options.icon;
                        c.confirmButtonText = options.confirmButtonText ?? '';
                        c.cancelButtonText = options.cancelButtonText ?? '';
                        c.showCancelButton = options.showCancelButton ?? true;
                        c.showDenyButton = options.showDenyButton ?? false;
                        c.denyButtonText = options.denyButtonText ?? '';
                        c.result.pipe(takeUntil(destroy$)).subscribe(result => {
                            destroy$.next();
                            destroy$.complete();
                            this.modalService.dismiss(c);
                            resolve(result.type === 'confirm');
                        });
                    },
                    onDismiss: () => {
                        destroy$.next();
                        destroy$.complete();
                        resolve(false);
                    },
                })
                .then();
        });
    }

    public confirmWithDeny(
        options: ConfirmDialogOptions
    ): Promise<'confirm' | 'deny' | 'cancel'> {
        return new Promise<'confirm' | 'deny' | 'cancel'>(resolve => {
            const destroy$ = new Subject<void>();

            this.modalService
                .show(ConfirmDialogComponent, {
                    title: options.title,
                    beforeInit: c => {
                        c.mode = 'confirm';
                        c.text = options.text;
                        c.icon = options.icon;
                        c.confirmButtonText = options.confirmButtonText ?? '';
                        c.cancelButtonText = options.cancelButtonText ?? '';
                        c.showCancelButton = options.showCancelButton ?? true;
                        c.showDenyButton = options.showDenyButton ?? false;
                        c.denyButtonText = options.denyButtonText ?? '';
                        c.result.pipe(takeUntil(destroy$)).subscribe(result => {
                            destroy$.next();
                            destroy$.complete();
                            this.modalService.dismiss(c);
                            resolve(result.type as any);
                        });
                    },
                    onDismiss: () => {
                        destroy$.next();
                        destroy$.complete();
                        resolve('cancel');
                    },
                })
                .then();
        });
    }

    public alert(options: AlertDialogOptions): Promise<void> {
        return new Promise<void>(resolve => {
            const destroy$ = new Subject<void>();

            this.modalService
                .show(ConfirmDialogComponent, {
                    title: options.title,
                    size: options.size,
                    beforeInit: c => {
                        c.mode = 'alert';
                        c.text = options.text;
                        c.icon = options.icon;
                        c.confirmButtonText = options.confirmButtonText ?? '';
                        c.showCancelButton = false;
                        c.result.pipe(takeUntil(destroy$)).subscribe(() => {
                            destroy$.next();
                            destroy$.complete();
                            this.modalService.dismiss(c);
                            resolve();
                        });
                    },
                    onDismiss: () => {
                        destroy$.next();
                        destroy$.complete();
                        resolve();
                    },
                })
                .then();
        });
    }

    public multiButton(
        options: MultiButtonDialogOptions
    ): Promise<string | null> {
        return new Promise<string | null>(resolve => {
            const destroy$ = new Subject<void>();

            this.modalService
                .show(ConfirmDialogComponent, {
                    title: options.title,
                    disableAutoDismiss: options.allowOutsideClick === false,
                    beforeInit: c => {
                        c.mode = 'multi';
                        c.text = options.text;
                        c.icon = options.icon;
                        c.buttons = options.buttons;
                        c.timer = options.timer ?? 0;
                        c.timerProgressBar = options.timerProgressBar ?? false;
                        c.result.pipe(takeUntil(destroy$)).subscribe(result => {
                            destroy$.next();
                            destroy$.complete();
                            this.modalService.dismiss(c);
                            if (result.type === 'timer') {
                                resolve(null);
                            } else {
                                resolve(result.key);
                            }
                        });
                    },
                    onDismiss: () => {
                        destroy$.next();
                        destroy$.complete();
                        resolve(null);
                    },
                })
                .then();
        });
    }

    public input<T = string>(options: InputDialogOptions): Promise<T | null> {
        return new Promise<T | null>(resolve => {
            const destroy$ = new Subject<void>();

            this.modalService
                .show(ConfirmDialogComponent, {
                    title: options.title,
                    beforeInit: c => {
                        c.mode = 'input';
                        c.inputType = options.inputType;
                        c.inputLabel = options.inputLabel;
                        c.inputPlaceholder = options.inputPlaceholder;
                        c.inputValue =
                            options.inputValue ??
                            (options.inputType === 'range' ? 0 : '');
                        c.confirmButtonText = options.confirmButtonText ?? '';
                        c.cancelButtonText = options.cancelButtonText ?? '';
                        c.showCancelButton = options.showCancelButton ?? true;

                        if (options.inputAttributes) {
                            c.inputMin = +(options.inputAttributes.min ?? 0);
                            c.inputMax = +(options.inputAttributes.max ?? 100);
                            c.inputStep = +(options.inputAttributes.step ?? 1);
                        }

                        c.result.pipe(takeUntil(destroy$)).subscribe(result => {
                            destroy$.next();
                            destroy$.complete();
                            this.modalService.dismiss(c);
                            if (result.type === 'confirm') {
                                resolve(result.value as T);
                            } else {
                                resolve(null);
                            }
                        });
                    },
                    onDismiss: () => {
                        destroy$.next();
                        destroy$.complete();
                        resolve(null);
                    },
                })
                .then();
        });
    }
}
